/**
 * Maps every `@primer/octicons-react` icon name to a per-icon dynamic import.
 *
 * Enumerating each icon with a literal specifier keeps lazy loading portable
 * across bundlers: both Vite (tests/dev) and rspack (production) can statically
 * analyse each `import('@primer/octicons-react/<Name>')` and code-split it into
 * its own on-demand chunk. A fully-dynamic import with a runtime `${name}`
 * expression cannot be analysed by Vite, so this map is the portable alternative.
 *
 * Rspack _does_ support fully-dynamic imports, but the map is still useful for type safety.
 * For Vite we _could use_ `import.meta.glob('@primer/octicons-react/*.mjs')` to generate the map, but that would increase the byte count significantly.
 * and not be portable to rspack.
 *
 * Each per-icon module re-exports its icon as `default`, so the loader can be
 * passed straight to `lazy()`.
 *
 * Generated from the installed `@primer/octicons-react` package — regenerate it
 * when the icon set changes so every icon stays covered.
 *
 */
import {lazyWithPreload as lazy} from '@github-ui/react-core/lazy-with-preload'
import type {Icon} from '@primer/octicons-react'

export type OcticonLoader = () => Promise<{default: Icon}>

/** A valid `@primer/octicons-react` icon component name, e.g. `"AlertIcon"`. */
export type OcticonName = Exclude<keyof typeof import('@primer/octicons-react'), 'default'>

/** An icon that renders lazily and can be fetched ahead of use via `preload()`. */
export type PreloadableIcon = Icon & {preload: () => Promise<void>}

export const octiconsMap: Record<OcticonName, PreloadableIcon> = {
  AccessibilityIcon: lazy(() => import('@primer/octicons-react/AccessibilityIcon')),
  AccessibilityInsetIcon: lazy(() => import('@primer/octicons-react/AccessibilityInsetIcon')),
  AgentIcon: lazy(() => import('@primer/octicons-react/AgentIcon')),
  AiModelIcon: lazy(() => import('@primer/octicons-react/AiModelIcon')),
  AlertIcon: lazy(() => import('@primer/octicons-react/AlertIcon')),
  AlertFillIcon: lazy(() => import('@primer/octicons-react/AlertFillIcon')),
  AppsIcon: lazy(() => import('@primer/octicons-react/AppsIcon')),
  ArchiveIcon: lazy(() => import('@primer/octicons-react/ArchiveIcon')),
  ArrowBothIcon: lazy(() => import('@primer/octicons-react/ArrowBothIcon')),
  ArrowDownIcon: lazy(() => import('@primer/octicons-react/ArrowDownIcon')),
  ArrowDownLeftIcon: lazy(() => import('@primer/octicons-react/ArrowDownLeftIcon')),
  ArrowDownRightIcon: lazy(() => import('@primer/octicons-react/ArrowDownRightIcon')),
  ArrowLeftIcon: lazy(() => import('@primer/octicons-react/ArrowLeftIcon')),
  ArrowRightIcon: lazy(() => import('@primer/octicons-react/ArrowRightIcon')),
  ArrowSwitchIcon: lazy(() => import('@primer/octicons-react/ArrowSwitchIcon')),
  ArrowUpIcon: lazy(() => import('@primer/octicons-react/ArrowUpIcon')),
  ArrowUpLeftIcon: lazy(() => import('@primer/octicons-react/ArrowUpLeftIcon')),
  ArrowUpRightIcon: lazy(() => import('@primer/octicons-react/ArrowUpRightIcon')),
  BeakerIcon: lazy(() => import('@primer/octicons-react/BeakerIcon')),
  BellIcon: lazy(() => import('@primer/octicons-react/BellIcon')),
  BellFillIcon: lazy(() => import('@primer/octicons-react/BellFillIcon')),
  BellSlashIcon: lazy(() => import('@primer/octicons-react/BellSlashIcon')),
  BlockedIcon: lazy(() => import('@primer/octicons-react/BlockedIcon')),
  BoldIcon: lazy(() => import('@primer/octicons-react/BoldIcon')),
  BookIcon: lazy(() => import('@primer/octicons-react/BookIcon')),
  BookLockedIcon: lazy(() => import('@primer/octicons-react/BookLockedIcon')),
  BookmarkIcon: lazy(() => import('@primer/octicons-react/BookmarkIcon')),
  BookmarkFillIcon: lazy(() => import('@primer/octicons-react/BookmarkFillIcon')),
  BookmarkFilledIcon: lazy(() => import('@primer/octicons-react/BookmarkFilledIcon')),
  BookmarkSlashIcon: lazy(() => import('@primer/octicons-react/BookmarkSlashIcon')),
  BookmarkSlashFillIcon: lazy(() => import('@primer/octicons-react/BookmarkSlashFillIcon')),
  BooleanOffIcon: lazy(() => import('@primer/octicons-react/BooleanOffIcon')),
  BooleanOnIcon: lazy(() => import('@primer/octicons-react/BooleanOnIcon')),
  BriefcaseIcon: lazy(() => import('@primer/octicons-react/BriefcaseIcon')),
  BroadcastIcon: lazy(() => import('@primer/octicons-react/BroadcastIcon')),
  BrowserIcon: lazy(() => import('@primer/octicons-react/BrowserIcon')),
  BugIcon: lazy(() => import('@primer/octicons-react/BugIcon')),
  CacheIcon: lazy(() => import('@primer/octicons-react/CacheIcon')),
  CalendarIcon: lazy(() => import('@primer/octicons-react/CalendarIcon')),
  CheckIcon: lazy(() => import('@primer/octicons-react/CheckIcon')),
  CheckCircleIcon: lazy(() => import('@primer/octicons-react/CheckCircleIcon')),
  CheckCircleFillIcon: lazy(() => import('@primer/octicons-react/CheckCircleFillIcon')),
  CheckboxIcon: lazy(() => import('@primer/octicons-react/CheckboxIcon')),
  CheckboxFillIcon: lazy(() => import('@primer/octicons-react/CheckboxFillIcon')),
  ChecklistIcon: lazy(() => import('@primer/octicons-react/ChecklistIcon')),
  ChevronDownIcon: lazy(() => import('@primer/octicons-react/ChevronDownIcon')),
  ChevronLeftIcon: lazy(() => import('@primer/octicons-react/ChevronLeftIcon')),
  ChevronRightIcon: lazy(() => import('@primer/octicons-react/ChevronRightIcon')),
  ChevronUpIcon: lazy(() => import('@primer/octicons-react/ChevronUpIcon')),
  CircleIcon: lazy(() => import('@primer/octicons-react/CircleIcon')),
  CircleSlashIcon: lazy(() => import('@primer/octicons-react/CircleSlashIcon')),
  ClockIcon: lazy(() => import('@primer/octicons-react/ClockIcon')),
  ClockFillIcon: lazy(() => import('@primer/octicons-react/ClockFillIcon')),
  CloudIcon: lazy(() => import('@primer/octicons-react/CloudIcon')),
  CloudOfflineIcon: lazy(() => import('@primer/octicons-react/CloudOfflineIcon')),
  CodeIcon: lazy(() => import('@primer/octicons-react/CodeIcon')),
  CodeOfConductIcon: lazy(() => import('@primer/octicons-react/CodeOfConductIcon')),
  CodeReviewIcon: lazy(() => import('@primer/octicons-react/CodeReviewIcon')),
  CodeSquareIcon: lazy(() => import('@primer/octicons-react/CodeSquareIcon')),
  CodescanIcon: lazy(() => import('@primer/octicons-react/CodescanIcon')),
  CodescanCheckmarkIcon: lazy(() => import('@primer/octicons-react/CodescanCheckmarkIcon')),
  CodespacesIcon: lazy(() => import('@primer/octicons-react/CodespacesIcon')),
  ColumnsIcon: lazy(() => import('@primer/octicons-react/ColumnsIcon')),
  CommandPaletteIcon: lazy(() => import('@primer/octicons-react/CommandPaletteIcon')),
  CommentIcon: lazy(() => import('@primer/octicons-react/CommentIcon')),
  CommentAiIcon: lazy(() => import('@primer/octicons-react/CommentAiIcon')),
  CommentDiscussionIcon: lazy(() => import('@primer/octicons-react/CommentDiscussionIcon')),
  CommentLockedIcon: lazy(() => import('@primer/octicons-react/CommentLockedIcon')),
  ComposeIcon: lazy(() => import('@primer/octicons-react/ComposeIcon')),
  ContainerIcon: lazy(() => import('@primer/octicons-react/ContainerIcon')),
  CopilotIcon: lazy(() => import('@primer/octicons-react/CopilotIcon')),
  CopilotErrorIcon: lazy(() => import('@primer/octicons-react/CopilotErrorIcon')),
  CopilotWarningIcon: lazy(() => import('@primer/octicons-react/CopilotWarningIcon')),
  CopyIcon: lazy(() => import('@primer/octicons-react/CopyIcon')),
  CpuIcon: lazy(() => import('@primer/octicons-react/CpuIcon')),
  CreditCardIcon: lazy(() => import('@primer/octicons-react/CreditCardIcon')),
  CrossReferenceIcon: lazy(() => import('@primer/octicons-react/CrossReferenceIcon')),
  CrosshairsIcon: lazy(() => import('@primer/octicons-react/CrosshairsIcon')),
  DashIcon: lazy(() => import('@primer/octicons-react/DashIcon')),
  DatabaseIcon: lazy(() => import('@primer/octicons-react/DatabaseIcon')),
  DependabotIcon: lazy(() => import('@primer/octicons-react/DependabotIcon')),
  DesktopDownloadIcon: lazy(() => import('@primer/octicons-react/DesktopDownloadIcon')),
  DeviceCameraIcon: lazy(() => import('@primer/octicons-react/DeviceCameraIcon')),
  DeviceCameraVideoIcon: lazy(() => import('@primer/octicons-react/DeviceCameraVideoIcon')),
  DeviceDesktopIcon: lazy(() => import('@primer/octicons-react/DeviceDesktopIcon')),
  DeviceMobileIcon: lazy(() => import('@primer/octicons-react/DeviceMobileIcon')),
  DevicesIcon: lazy(() => import('@primer/octicons-react/DevicesIcon')),
  DiamondIcon: lazy(() => import('@primer/octicons-react/DiamondIcon')),
  DiceIcon: lazy(() => import('@primer/octicons-react/DiceIcon')),
  DiffIcon: lazy(() => import('@primer/octicons-react/DiffIcon')),
  DiffAddedIcon: lazy(() => import('@primer/octicons-react/DiffAddedIcon')),
  DiffIgnoredIcon: lazy(() => import('@primer/octicons-react/DiffIgnoredIcon')),
  DiffModifiedIcon: lazy(() => import('@primer/octicons-react/DiffModifiedIcon')),
  DiffRemovedIcon: lazy(() => import('@primer/octicons-react/DiffRemovedIcon')),
  DiffRenamedIcon: lazy(() => import('@primer/octicons-react/DiffRenamedIcon')),
  DiscussionClosedIcon: lazy(() => import('@primer/octicons-react/DiscussionClosedIcon')),
  DiscussionDuplicateIcon: lazy(() => import('@primer/octicons-react/DiscussionDuplicateIcon')),
  DiscussionOutdatedIcon: lazy(() => import('@primer/octicons-react/DiscussionOutdatedIcon')),
  DotIcon: lazy(() => import('@primer/octicons-react/DotIcon')),
  DotFillIcon: lazy(() => import('@primer/octicons-react/DotFillIcon')),
  DownloadIcon: lazy(() => import('@primer/octicons-react/DownloadIcon')),
  DuplicateIcon: lazy(() => import('@primer/octicons-react/DuplicateIcon')),
  EllipsisIcon: lazy(() => import('@primer/octicons-react/EllipsisIcon')),
  ExclamationIcon: lazy(() => import('@primer/octicons-react/ExclamationIcon')),
  EyeIcon: lazy(() => import('@primer/octicons-react/EyeIcon')),
  EyeClosedIcon: lazy(() => import('@primer/octicons-react/EyeClosedIcon')),
  FeedDiscussionIcon: lazy(() => import('@primer/octicons-react/FeedDiscussionIcon')),
  FeedForkedIcon: lazy(() => import('@primer/octicons-react/FeedForkedIcon')),
  FeedHeartIcon: lazy(() => import('@primer/octicons-react/FeedHeartIcon')),
  FeedIssueClosedIcon: lazy(() => import('@primer/octicons-react/FeedIssueClosedIcon')),
  FeedIssueDraftIcon: lazy(() => import('@primer/octicons-react/FeedIssueDraftIcon')),
  FeedIssueOpenIcon: lazy(() => import('@primer/octicons-react/FeedIssueOpenIcon')),
  FeedIssueReopenIcon: lazy(() => import('@primer/octicons-react/FeedIssueReopenIcon')),
  FeedMergedIcon: lazy(() => import('@primer/octicons-react/FeedMergedIcon')),
  FeedPersonIcon: lazy(() => import('@primer/octicons-react/FeedPersonIcon')),
  FeedPlusIcon: lazy(() => import('@primer/octicons-react/FeedPlusIcon')),
  FeedPublicIcon: lazy(() => import('@primer/octicons-react/FeedPublicIcon')),
  FeedPullRequestClosedIcon: lazy(() => import('@primer/octicons-react/FeedPullRequestClosedIcon')),
  FeedPullRequestDraftIcon: lazy(() => import('@primer/octicons-react/FeedPullRequestDraftIcon')),
  FeedPullRequestOpenIcon: lazy(() => import('@primer/octicons-react/FeedPullRequestOpenIcon')),
  FeedRepoIcon: lazy(() => import('@primer/octicons-react/FeedRepoIcon')),
  FeedRocketIcon: lazy(() => import('@primer/octicons-react/FeedRocketIcon')),
  FeedStarIcon: lazy(() => import('@primer/octicons-react/FeedStarIcon')),
  FeedTagIcon: lazy(() => import('@primer/octicons-react/FeedTagIcon')),
  FeedTrophyIcon: lazy(() => import('@primer/octicons-react/FeedTrophyIcon')),
  FileIcon: lazy(() => import('@primer/octicons-react/FileIcon')),
  FileAddedIcon: lazy(() => import('@primer/octicons-react/FileAddedIcon')),
  FileBadgeIcon: lazy(() => import('@primer/octicons-react/FileBadgeIcon')),
  FileBinaryIcon: lazy(() => import('@primer/octicons-react/FileBinaryIcon')),
  FileCheckIcon: lazy(() => import('@primer/octicons-react/FileCheckIcon')),
  FileCodeIcon: lazy(() => import('@primer/octicons-react/FileCodeIcon')),
  FileDiffIcon: lazy(() => import('@primer/octicons-react/FileDiffIcon')),
  FileDirectoryIcon: lazy(() => import('@primer/octicons-react/FileDirectoryIcon')),
  FileDirectoryFillIcon: lazy(() => import('@primer/octicons-react/FileDirectoryFillIcon')),
  FileDirectoryOpenFillIcon: lazy(() => import('@primer/octicons-react/FileDirectoryOpenFillIcon')),
  FileDirectorySymlinkIcon: lazy(() => import('@primer/octicons-react/FileDirectorySymlinkIcon')),
  FileMediaIcon: lazy(() => import('@primer/octicons-react/FileMediaIcon')),
  FileMovedIcon: lazy(() => import('@primer/octicons-react/FileMovedIcon')),
  FileRemovedIcon: lazy(() => import('@primer/octicons-react/FileRemovedIcon')),
  FileSubmoduleIcon: lazy(() => import('@primer/octicons-react/FileSubmoduleIcon')),
  FileSymlinkFileIcon: lazy(() => import('@primer/octicons-react/FileSymlinkFileIcon')),
  FileZipIcon: lazy(() => import('@primer/octicons-react/FileZipIcon')),
  FilterIcon: lazy(() => import('@primer/octicons-react/FilterIcon')),
  FilterRemoveIcon: lazy(() => import('@primer/octicons-react/FilterRemoveIcon')),
  FiscalHostIcon: lazy(() => import('@primer/octicons-react/FiscalHostIcon')),
  FlagIcon: lazy(() => import('@primer/octicons-react/FlagIcon')),
  FlameIcon: lazy(() => import('@primer/octicons-react/FlameIcon')),
  FlowchartIcon: lazy(() => import('@primer/octicons-react/FlowchartIcon')),
  FocusCenterIcon: lazy(() => import('@primer/octicons-react/FocusCenterIcon')),
  FoldIcon: lazy(() => import('@primer/octicons-react/FoldIcon')),
  FoldDownIcon: lazy(() => import('@primer/octicons-react/FoldDownIcon')),
  FoldUpIcon: lazy(() => import('@primer/octicons-react/FoldUpIcon')),
  GearIcon: lazy(() => import('@primer/octicons-react/GearIcon')),
  GiftIcon: lazy(() => import('@primer/octicons-react/GiftIcon')),
  GitBranchIcon: lazy(() => import('@primer/octicons-react/GitBranchIcon')),
  GitBranchCheckIcon: lazy(() => import('@primer/octicons-react/GitBranchCheckIcon')),
  GitCommitIcon: lazy(() => import('@primer/octicons-react/GitCommitIcon')),
  GitCompareIcon: lazy(() => import('@primer/octicons-react/GitCompareIcon')),
  GitMergeIcon: lazy(() => import('@primer/octicons-react/GitMergeIcon')),
  GitMergeQueueIcon: lazy(() => import('@primer/octicons-react/GitMergeQueueIcon')),
  GitPullRequestIcon: lazy(() => import('@primer/octicons-react/GitPullRequestIcon')),
  GitPullRequestClosedIcon: lazy(() => import('@primer/octicons-react/GitPullRequestClosedIcon')),
  GitPullRequestDraftIcon: lazy(() => import('@primer/octicons-react/GitPullRequestDraftIcon')),
  GitPullRequestLockedIcon: lazy(() => import('@primer/octicons-react/GitPullRequestLockedIcon')),
  GlobeIcon: lazy(() => import('@primer/octicons-react/GlobeIcon')),
  GoalIcon: lazy(() => import('@primer/octicons-react/GoalIcon')),
  GrabberIcon: lazy(() => import('@primer/octicons-react/GrabberIcon')),
  GraphIcon: lazy(() => import('@primer/octicons-react/GraphIcon')),
  GraphBarHorizontalIcon: lazy(() => import('@primer/octicons-react/GraphBarHorizontalIcon')),
  GraphBarVerticalIcon: lazy(() => import('@primer/octicons-react/GraphBarVerticalIcon')),
  GraphStackedAreaIcon: lazy(() => import('@primer/octicons-react/GraphStackedAreaIcon')),
  HashIcon: lazy(() => import('@primer/octicons-react/HashIcon')),
  HeadingIcon: lazy(() => import('@primer/octicons-react/HeadingIcon')),
  HeartIcon: lazy(() => import('@primer/octicons-react/HeartIcon')),
  HeartFillIcon: lazy(() => import('@primer/octicons-react/HeartFillIcon')),
  HistoryIcon: lazy(() => import('@primer/octicons-react/HistoryIcon')),
  HomeIcon: lazy(() => import('@primer/octicons-react/HomeIcon')),
  HomeFillIcon: lazy(() => import('@primer/octicons-react/HomeFillIcon')),
  HorizontalRuleIcon: lazy(() => import('@primer/octicons-react/HorizontalRuleIcon')),
  HourglassIcon: lazy(() => import('@primer/octicons-react/HourglassIcon')),
  HubotIcon: lazy(() => import('@primer/octicons-react/HubotIcon')),
  IdBadgeIcon: lazy(() => import('@primer/octicons-react/IdBadgeIcon')),
  ImageIcon: lazy(() => import('@primer/octicons-react/ImageIcon')),
  InboxIcon: lazy(() => import('@primer/octicons-react/InboxIcon')),
  InboxFillIcon: lazy(() => import('@primer/octicons-react/InboxFillIcon')),
  InfinityIcon: lazy(() => import('@primer/octicons-react/InfinityIcon')),
  InfoIcon: lazy(() => import('@primer/octicons-react/InfoIcon')),
  IssueClosedIcon: lazy(() => import('@primer/octicons-react/IssueClosedIcon')),
  IssueDraftIcon: lazy(() => import('@primer/octicons-react/IssueDraftIcon')),
  IssueLockedIcon: lazy(() => import('@primer/octicons-react/IssueLockedIcon')),
  IssueOpenedIcon: lazy(() => import('@primer/octicons-react/IssueOpenedIcon')),
  IssueReopenedIcon: lazy(() => import('@primer/octicons-react/IssueReopenedIcon')),
  IssueTrackedByIcon: lazy(() => import('@primer/octicons-react/IssueTrackedByIcon')),
  IssueTracksIcon: lazy(() => import('@primer/octicons-react/IssueTracksIcon')),
  ItalicIcon: lazy(() => import('@primer/octicons-react/ItalicIcon')),
  IterationsIcon: lazy(() => import('@primer/octicons-react/IterationsIcon')),
  KebabHorizontalIcon: lazy(() => import('@primer/octicons-react/KebabHorizontalIcon')),
  KeyIcon: lazy(() => import('@primer/octicons-react/KeyIcon')),
  KeyAsteriskIcon: lazy(() => import('@primer/octicons-react/KeyAsteriskIcon')),
  LawIcon: lazy(() => import('@primer/octicons-react/LawIcon')),
  LightBulbIcon: lazy(() => import('@primer/octicons-react/LightBulbIcon')),
  LinkIcon: lazy(() => import('@primer/octicons-react/LinkIcon')),
  LinkExternalIcon: lazy(() => import('@primer/octicons-react/LinkExternalIcon')),
  ListOrderedIcon: lazy(() => import('@primer/octicons-react/ListOrderedIcon')),
  ListUnorderedIcon: lazy(() => import('@primer/octicons-react/ListUnorderedIcon')),
  LocationIcon: lazy(() => import('@primer/octicons-react/LocationIcon')),
  LockIcon: lazy(() => import('@primer/octicons-react/LockIcon')),
  LockupGithubIcon: lazy(() => import('@primer/octicons-react/LockupGithubIcon')),
  LogIcon: lazy(() => import('@primer/octicons-react/LogIcon')),
  LogoGistIcon: lazy(() => import('@primer/octicons-react/LogoGistIcon')),
  LogoGithubIcon: lazy(() => import('@primer/octicons-react/LogoGithubIcon')),
  LoopIcon: lazy(() => import('@primer/octicons-react/LoopIcon')),
  MailIcon: lazy(() => import('@primer/octicons-react/MailIcon')),
  MarkGithubIcon: lazy(() => import('@primer/octicons-react/MarkGithubIcon')),
  MarkdownIcon: lazy(() => import('@primer/octicons-react/MarkdownIcon')),
  MaximizeIcon: lazy(() => import('@primer/octicons-react/MaximizeIcon')),
  McpIcon: lazy(() => import('@primer/octicons-react/McpIcon')),
  MegaphoneIcon: lazy(() => import('@primer/octicons-react/MegaphoneIcon')),
  MentionIcon: lazy(() => import('@primer/octicons-react/MentionIcon')),
  MeterIcon: lazy(() => import('@primer/octicons-react/MeterIcon')),
  MilestoneIcon: lazy(() => import('@primer/octicons-react/MilestoneIcon')),
  MinimizeIcon: lazy(() => import('@primer/octicons-react/MinimizeIcon')),
  MirrorIcon: lazy(() => import('@primer/octicons-react/MirrorIcon')),
  MoonIcon: lazy(() => import('@primer/octicons-react/MoonIcon')),
  MortarBoardIcon: lazy(() => import('@primer/octicons-react/MortarBoardIcon')),
  MoveToBottomIcon: lazy(() => import('@primer/octicons-react/MoveToBottomIcon')),
  MoveToEndIcon: lazy(() => import('@primer/octicons-react/MoveToEndIcon')),
  MoveToStartIcon: lazy(() => import('@primer/octicons-react/MoveToStartIcon')),
  MoveToTopIcon: lazy(() => import('@primer/octicons-react/MoveToTopIcon')),
  MultiSelectIcon: lazy(() => import('@primer/octicons-react/MultiSelectIcon')),
  MuteIcon: lazy(() => import('@primer/octicons-react/MuteIcon')),
  NoEntryIcon: lazy(() => import('@primer/octicons-react/NoEntryIcon')),
  NoEntryFillIcon: lazy(() => import('@primer/octicons-react/NoEntryFillIcon')),
  NodeIcon: lazy(() => import('@primer/octicons-react/NodeIcon')),
  NorthStarIcon: lazy(() => import('@primer/octicons-react/NorthStarIcon')),
  NoteIcon: lazy(() => import('@primer/octicons-react/NoteIcon')),
  NumberIcon: lazy(() => import('@primer/octicons-react/NumberIcon')),
  OrganizationIcon: lazy(() => import('@primer/octicons-react/OrganizationIcon')),
  PackageIcon: lazy(() => import('@primer/octicons-react/PackageIcon')),
  PackageDependenciesIcon: lazy(() => import('@primer/octicons-react/PackageDependenciesIcon')),
  PackageDependentsIcon: lazy(() => import('@primer/octicons-react/PackageDependentsIcon')),
  PaintbrushIcon: lazy(() => import('@primer/octicons-react/PaintbrushIcon')),
  PaperAirplaneIcon: lazy(() => import('@primer/octicons-react/PaperAirplaneIcon')),
  PaperclipIcon: lazy(() => import('@primer/octicons-react/PaperclipIcon')),
  PasskeyFillIcon: lazy(() => import('@primer/octicons-react/PasskeyFillIcon')),
  PasteIcon: lazy(() => import('@primer/octicons-react/PasteIcon')),
  PauseIcon: lazy(() => import('@primer/octicons-react/PauseIcon')),
  PencilIcon: lazy(() => import('@primer/octicons-react/PencilIcon')),
  PencilAiIcon: lazy(() => import('@primer/octicons-react/PencilAiIcon')),
  PeopleIcon: lazy(() => import('@primer/octicons-react/PeopleIcon')),
  PersonIcon: lazy(() => import('@primer/octicons-react/PersonIcon')),
  PersonAddIcon: lazy(() => import('@primer/octicons-react/PersonAddIcon')),
  PersonFillIcon: lazy(() => import('@primer/octicons-react/PersonFillIcon')),
  PinIcon: lazy(() => import('@primer/octicons-react/PinIcon')),
  PinSlashIcon: lazy(() => import('@primer/octicons-react/PinSlashIcon')),
  PivotColumnIcon: lazy(() => import('@primer/octicons-react/PivotColumnIcon')),
  PlayIcon: lazy(() => import('@primer/octicons-react/PlayIcon')),
  PlugIcon: lazy(() => import('@primer/octicons-react/PlugIcon')),
  PlusIcon: lazy(() => import('@primer/octicons-react/PlusIcon')),
  PlusCircleIcon: lazy(() => import('@primer/octicons-react/PlusCircleIcon')),
  ProjectIcon: lazy(() => import('@primer/octicons-react/ProjectIcon')),
  ProjectCheckIcon: lazy(() => import('@primer/octicons-react/ProjectCheckIcon')),
  ProjectRoadmapIcon: lazy(() => import('@primer/octicons-react/ProjectRoadmapIcon')),
  ProjectSymlinkIcon: lazy(() => import('@primer/octicons-react/ProjectSymlinkIcon')),
  ProjectTemplateIcon: lazy(() => import('@primer/octicons-react/ProjectTemplateIcon')),
  PulseIcon: lazy(() => import('@primer/octicons-react/PulseIcon')),
  QuestionIcon: lazy(() => import('@primer/octicons-react/QuestionIcon')),
  QuestionBubbleIcon: lazy(() => import('@primer/octicons-react/QuestionBubbleIcon')),
  QuoteIcon: lazy(() => import('@primer/octicons-react/QuoteIcon')),
  ReadIcon: lazy(() => import('@primer/octicons-react/ReadIcon')),
  RedoIcon: lazy(() => import('@primer/octicons-react/RedoIcon')),
  RelFilePathIcon: lazy(() => import('@primer/octicons-react/RelFilePathIcon')),
  ReplyIcon: lazy(() => import('@primer/octicons-react/ReplyIcon')),
  RepoIcon: lazy(() => import('@primer/octicons-react/RepoIcon')),
  RepoCloneIcon: lazy(() => import('@primer/octicons-react/RepoCloneIcon')),
  RepoDeleteIcon: lazy(() => import('@primer/octicons-react/RepoDeleteIcon')),
  RepoDeletedIcon: lazy(() => import('@primer/octicons-react/RepoDeletedIcon')),
  RepoForkedIcon: lazy(() => import('@primer/octicons-react/RepoForkedIcon')),
  RepoForkedLockedIcon: lazy(() => import('@primer/octicons-react/RepoForkedLockedIcon')),
  RepoLockedIcon: lazy(() => import('@primer/octicons-react/RepoLockedIcon')),
  RepoPullIcon: lazy(() => import('@primer/octicons-react/RepoPullIcon')),
  RepoPushIcon: lazy(() => import('@primer/octicons-react/RepoPushIcon')),
  RepoTemplateIcon: lazy(() => import('@primer/octicons-react/RepoTemplateIcon')),
  ReportIcon: lazy(() => import('@primer/octicons-react/ReportIcon')),
  RocketIcon: lazy(() => import('@primer/octicons-react/RocketIcon')),
  RowsIcon: lazy(() => import('@primer/octicons-react/RowsIcon')),
  RssIcon: lazy(() => import('@primer/octicons-react/RssIcon')),
  RubyIcon: lazy(() => import('@primer/octicons-react/RubyIcon')),
  SandboxIcon: lazy(() => import('@primer/octicons-react/SandboxIcon')),
  ScreenFullIcon: lazy(() => import('@primer/octicons-react/ScreenFullIcon')),
  ScreenNormalIcon: lazy(() => import('@primer/octicons-react/ScreenNormalIcon')),
  SearchIcon: lazy(() => import('@primer/octicons-react/SearchIcon')),
  ServerIcon: lazy(() => import('@primer/octicons-react/ServerIcon')),
  ShareIcon: lazy(() => import('@primer/octicons-react/ShareIcon')),
  ShareAndroidIcon: lazy(() => import('@primer/octicons-react/ShareAndroidIcon')),
  ShieldIcon: lazy(() => import('@primer/octicons-react/ShieldIcon')),
  ShieldCheckIcon: lazy(() => import('@primer/octicons-react/ShieldCheckIcon')),
  ShieldLockIcon: lazy(() => import('@primer/octicons-react/ShieldLockIcon')),
  ShieldSlashIcon: lazy(() => import('@primer/octicons-react/ShieldSlashIcon')),
  ShieldXIcon: lazy(() => import('@primer/octicons-react/ShieldXIcon')),
  SidebarCollapseIcon: lazy(() => import('@primer/octicons-react/SidebarCollapseIcon')),
  SidebarExpandIcon: lazy(() => import('@primer/octicons-react/SidebarExpandIcon')),
  SignInIcon: lazy(() => import('@primer/octicons-react/SignInIcon')),
  SignOutIcon: lazy(() => import('@primer/octicons-react/SignOutIcon')),
  SingleSelectIcon: lazy(() => import('@primer/octicons-react/SingleSelectIcon')),
  SkipIcon: lazy(() => import('@primer/octicons-react/SkipIcon')),
  SkipFillIcon: lazy(() => import('@primer/octicons-react/SkipFillIcon')),
  SlidersIcon: lazy(() => import('@primer/octicons-react/SlidersIcon')),
  SmileyIcon: lazy(() => import('@primer/octicons-react/SmileyIcon')),
  SmileyFrownIcon: lazy(() => import('@primer/octicons-react/SmileyFrownIcon')),
  SmileyFrustratedIcon: lazy(() => import('@primer/octicons-react/SmileyFrustratedIcon')),
  SmileyGrinIcon: lazy(() => import('@primer/octicons-react/SmileyGrinIcon')),
  SmileyNeutralIcon: lazy(() => import('@primer/octicons-react/SmileyNeutralIcon')),
  SortAscIcon: lazy(() => import('@primer/octicons-react/SortAscIcon')),
  SortDescIcon: lazy(() => import('@primer/octicons-react/SortDescIcon')),
  SpaceIcon: lazy(() => import('@primer/octicons-react/SpaceIcon')),
  SpacingLargeIcon: lazy(() => import('@primer/octicons-react/SpacingLargeIcon')),
  SpacingMediumIcon: lazy(() => import('@primer/octicons-react/SpacingMediumIcon')),
  SpacingSmallIcon: lazy(() => import('@primer/octicons-react/SpacingSmallIcon')),
  SparkleIcon: lazy(() => import('@primer/octicons-react/SparkleIcon')),
  SparkleFillIcon: lazy(() => import('@primer/octicons-react/SparkleFillIcon')),
  SparklesFillIcon: lazy(() => import('@primer/octicons-react/SparklesFillIcon')),
  SplitViewIcon: lazy(() => import('@primer/octicons-react/SplitViewIcon')),
  SponsorTiersIcon: lazy(() => import('@primer/octicons-react/SponsorTiersIcon')),
  SquareIcon: lazy(() => import('@primer/octicons-react/SquareIcon')),
  SquareCircleIcon: lazy(() => import('@primer/octicons-react/SquareCircleIcon')),
  SquareFillIcon: lazy(() => import('@primer/octicons-react/SquareFillIcon')),
  SquirrelIcon: lazy(() => import('@primer/octicons-react/SquirrelIcon')),
  StackIcon: lazy(() => import('@primer/octicons-react/StackIcon')),
  StackAddIcon: lazy(() => import('@primer/octicons-react/StackAddIcon')),
  StackCheckIcon: lazy(() => import('@primer/octicons-react/StackCheckIcon')),
  StackRemoveIcon: lazy(() => import('@primer/octicons-react/StackRemoveIcon')),
  StarIcon: lazy(() => import('@primer/octicons-react/StarIcon')),
  StarFillIcon: lazy(() => import('@primer/octicons-react/StarFillIcon')),
  StopIcon: lazy(() => import('@primer/octicons-react/StopIcon')),
  StopwatchIcon: lazy(() => import('@primer/octicons-react/StopwatchIcon')),
  StrikethroughIcon: lazy(() => import('@primer/octicons-react/StrikethroughIcon')),
  SunIcon: lazy(() => import('@primer/octicons-react/SunIcon')),
  SyncIcon: lazy(() => import('@primer/octicons-react/SyncIcon')),
  TabIcon: lazy(() => import('@primer/octicons-react/TabIcon')),
  TabExternalIcon: lazy(() => import('@primer/octicons-react/TabExternalIcon')),
  TableIcon: lazy(() => import('@primer/octicons-react/TableIcon')),
  TagIcon: lazy(() => import('@primer/octicons-react/TagIcon')),
  TasklistIcon: lazy(() => import('@primer/octicons-react/TasklistIcon')),
  TelescopeIcon: lazy(() => import('@primer/octicons-react/TelescopeIcon')),
  TelescopeFillIcon: lazy(() => import('@primer/octicons-react/TelescopeFillIcon')),
  TerminalIcon: lazy(() => import('@primer/octicons-react/TerminalIcon')),
  ThreeBarsIcon: lazy(() => import('@primer/octicons-react/ThreeBarsIcon')),
  ThumbsdownIcon: lazy(() => import('@primer/octicons-react/ThumbsdownIcon')),
  ThumbsupIcon: lazy(() => import('@primer/octicons-react/ThumbsupIcon')),
  ToolsIcon: lazy(() => import('@primer/octicons-react/ToolsIcon')),
  TrackedByClosedCompletedIcon: lazy(() => import('@primer/octicons-react/TrackedByClosedCompletedIcon')),
  TrackedByClosedNotPlannedIcon: lazy(() => import('@primer/octicons-react/TrackedByClosedNotPlannedIcon')),
  TrashIcon: lazy(() => import('@primer/octicons-react/TrashIcon')),
  TriangleDownIcon: lazy(() => import('@primer/octicons-react/TriangleDownIcon')),
  TriangleLeftIcon: lazy(() => import('@primer/octicons-react/TriangleLeftIcon')),
  TriangleRightIcon: lazy(() => import('@primer/octicons-react/TriangleRightIcon')),
  TriangleUpIcon: lazy(() => import('@primer/octicons-react/TriangleUpIcon')),
  TrophyIcon: lazy(() => import('@primer/octicons-react/TrophyIcon')),
  TypographyIcon: lazy(() => import('@primer/octicons-react/TypographyIcon')),
  UndoIcon: lazy(() => import('@primer/octicons-react/UndoIcon')),
  UnfoldIcon: lazy(() => import('@primer/octicons-react/UnfoldIcon')),
  UnlinkIcon: lazy(() => import('@primer/octicons-react/UnlinkIcon')),
  UnlockIcon: lazy(() => import('@primer/octicons-react/UnlockIcon')),
  UnmuteIcon: lazy(() => import('@primer/octicons-react/UnmuteIcon')),
  UnreadIcon: lazy(() => import('@primer/octicons-react/UnreadIcon')),
  UnverifiedIcon: lazy(() => import('@primer/octicons-react/UnverifiedIcon')),
  UnwrapIcon: lazy(() => import('@primer/octicons-react/UnwrapIcon')),
  UploadIcon: lazy(() => import('@primer/octicons-react/UploadIcon')),
  VerifiedIcon: lazy(() => import('@primer/octicons-react/VerifiedIcon')),
  VersionsIcon: lazy(() => import('@primer/octicons-react/VersionsIcon')),
  VideoIcon: lazy(() => import('@primer/octicons-react/VideoIcon')),
  ViewFilesIcon: lazy(() => import('@primer/octicons-react/ViewFilesIcon')),
  VscodeIcon: lazy(() => import('@primer/octicons-react/VscodeIcon')),
  WebhookIcon: lazy(() => import('@primer/octicons-react/WebhookIcon')),
  WorkflowIcon: lazy(() => import('@primer/octicons-react/WorkflowIcon')),
  WrapIcon: lazy(() => import('@primer/octicons-react/WrapIcon')),
  XIcon: lazy(() => import('@primer/octicons-react/XIcon')),
  XCircleIcon: lazy(() => import('@primer/octicons-react/XCircleIcon')),
  XCircleFillIcon: lazy(() => import('@primer/octicons-react/XCircleFillIcon')),
  ZapIcon: lazy(() => import('@primer/octicons-react/ZapIcon')),
  ZoomInIcon: lazy(() => import('@primer/octicons-react/ZoomInIcon')),
  ZoomOutIcon: lazy(() => import('@primer/octicons-react/ZoomOutIcon')),
}
